import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type JSX,
} from 'react';
import backButtonDark from '@/assets/svg/static/back-button-dark.svg';
import { SkeletonLoader } from '@/components/ui';
import { EVENTS, PROPERTIES, trackEvent } from '@/services/posthog';
import { fetchCurationById } from '@/services/slaydAPI';
import { generateCuratorDeeplink } from '@/services/slaydAPI/curations';
import useLayoutStore from '@/stores/useLayoutStore';
import type { Curation } from '@/types/Curation.types';
import { decodeBase64Url, updatedImageUrl } from '@/utils/formatting';
import { openCurationInApp } from '@/utils/openInApp';
import CurationInstallSheet from './CurationInstallSheet';
import {
  HEADER_HEIGHT,
  PRODUCTS_ON_INITIAL_LOAD,
  PRODUCTS_ON_LOAD_MORE,
  SCROLL_DIRECTION_THRESHOLD,
  SLUG_BAR_HEIGHT,
  SLUG_BAR_LOCK_PERIOD,
} from './CurationsPageV2.constants';
import styles from './CurationsPageV2.module.css';
import StaticLoadDoubleColumnProductList from './StaticLoadDoubleColumnProductList';

const CurationPageV2 = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [curationsData, setCurationsData] = useState<Curation[]>([]);
  const [showFixedSlugBar, setShowFixedSlugBar] = useState(false);
  const [lockFixedSlugBar, setLockFixedSlugBar] = useState(false);
  const [isScrollingUp, setIsScrollingUp] = useState(true);
  const title = useRef<string>('');
  const fetchingData = useRef<boolean>(false);
  const slugBarRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<{ [id: number]: HTMLDivElement | null }>({});
  const containerRef = useRef<HTMLDivElement | null>(null);
  const fixedSlugBarRef = useRef<HTMLDivElement | null>(null);
  const scrollLeftRef = useRef<number>(0);
  const lastScrollTop = useRef<number>(0);
  const fixedSlugBarTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showInstallSheet, setShowInstallSheet] = useState(false);
  const [overrideDeepLink, setOverrideDeepLink] = useState<
    string | undefined
  >();
  const [curatorLinkEventPayload, setCuratorLinkEventPayload] = useState<
    Record<string, string> | undefined
  >();
  const setNavbarVisibility = useLayoutStore(
    (state) => state.setNavbarVisibility
  );

  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  // Prevent duplicate POST on re-renders
  const hasPostedCuratorLinkRef = useRef<boolean>(false);
  // True when no key param (no deeplink fetch needed) or fetch has settled
  const [isDeeplinkReady, setIsDeeplinkReady] = useState<boolean>(
    () => !searchParams.get('key')
  );
  // Tracks if sheet was requested while deeplink fetch was in-flight
  const pendingInstallSheetRef = useRef<boolean>(false);

  // Extract key from URL and post to service
  useEffect(() => {
    if (!id) return;
    const key = searchParams.get('key');
    const rid = searchParams.get('rid') ?? '';

    if (!key) return;
    if (hasPostedCuratorLinkRef.current) return;

    const postLink = async (): Promise<void> => {
      try {
        let curatorName = '';
        let instagramName = '';
        let postId = '';
        const variant = searchParams.get('variant') ?? '';

        try {
          const decodedStr = decodeBase64Url(key);
          const trimmedDecodedStr = decodedStr.trim();
          const [
            parsedCuratorName = '',
            parsedInstagramName = '',
            parsedPostId = '',
          ] = trimmedDecodedStr.split('|');
          curatorName = parsedCuratorName;
          instagramName = parsedInstagramName;
          postId = parsedPostId;

          setCuratorLinkEventPayload({
            [PROPERTIES.CURATOR_NAME]: curatorName,
            [PROPERTIES.INSTAGRAM_ACCOUNT_NAME]: instagramName,
            [PROPERTIES.POST_ID]: postId,
            [PROPERTIES.CURATION_ID]: id ?? '',
            [PROPERTIES.EXPERIMENT]: variant,
            [PROPERTIES.DISTINCT_ID]: rid,
          });
        } catch (decodeError) {
          console.error(
            'Invalid Base64 key. Skipping curator link post.',
            decodeError
          );
          setIsDeeplinkReady(true);
          return;
        }

        hasPostedCuratorLinkRef.current = true;

        const response = await generateCuratorDeeplink({
          curatorName,
          instagramName,
          postId,
          curationId: id,
          variant,
        });
        const deepLinkWithRid = response?.deepLink
          ? `${response.deepLink}${response.deepLink.includes('?') ? '&' : '?'}rid=${rid}`
          : response?.deepLink;
        setOverrideDeepLink(deepLinkWithRid);
      } catch (error) {
        console.error('Error posting curator chutto link: ', error);
      } finally {
        setIsDeeplinkReady(true);
      }
    };

    postLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hide navbar when sheet is shown
  useEffect(() => {
    if (showInstallSheet) {
      setNavbarVisibility(false);
    }
    return () => {
      setNavbarVisibility(true);
    };
  }, [showInstallSheet, setNavbarVisibility]);

  // Flush pending sheet once deeplink fetch settles
  useEffect(() => {
    if (isDeeplinkReady && pendingInstallSheetRef.current) {
      pendingInstallSheetRef.current = false;
      setShowInstallSheet(true);
    }
  }, [isDeeplinkReady]);

  // Update showInstallPrompt to set local state instead of using the download store
  const showInstallPrompt = useCallback((): void => {
    // Don't show sheet if noSheet parameter is present in URL
    if (searchParams.get('noSheet') === 'true') {
      return;
    }
    if (!isDeeplinkReady) {
      pendingInstallSheetRef.current = true;
      return;
    }
    setShowInstallSheet(true);
  }, [searchParams, isDeeplinkReady]);

  // fetching data
  useEffect(() => {
    if (!id) return;

    const fetchCuration = async (): Promise<void> => {
      if (fetchingData.current) return;

      fetchingData.current = true;
      setIsLoading(true);

      try {
        const fetchedCurationData = await fetchCurationById(id);

        if (
          fetchedCurationData.relatedCurations &&
          fetchedCurationData.relatedCurations.length !== 0
        ) {
          const newCurationData = fetchedCurationData.relatedCurations.map(
            (curationData) => {
              return {
                ...curationData,
              };
            }
          );
          setCurationsData(newCurationData);
        } else {
          setCurationsData([fetchedCurationData]);
        }
        title.current = fetchedCurationData.title;
      } catch (error) {
        console.error('Error fetching curation data: ', error);
      } finally {
        fetchingData.current = false;
        setIsLoading(false);
      }
    };

    fetchCuration();
  }, [id]);

  // timeout for fixed slug bar to disappear after click
  const resetFixedSlugBarTimeout = (): void => {
    // Clear any existing timeout
    if (fixedSlugBarTimeoutRef.current) {
      clearTimeout(fixedSlugBarTimeoutRef.current);
    }
    // Set lock to true (show bar)
    setLockFixedSlugBar(true);
    // Set a new timeout to hide it after the period
    fixedSlugBarTimeoutRef.current = setTimeout(() => {
      setLockFixedSlugBar(false);
    }, SLUG_BAR_LOCK_PERIOD);
  };

  // cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (fixedSlugBarTimeoutRef.current) {
        clearTimeout(fixedSlugBarTimeoutRef.current);
      }
    };
  }, []);

  // user interactions with fixed slug bar
  useEffect(() => {
    const fixedBar = fixedSlugBarRef.current;
    if (!fixedBar) return;

    const handleUserInteraction = (): void => {
      resetFixedSlugBarTimeout();
    };

    fixedBar.addEventListener('scroll', handleUserInteraction);
    fixedBar.addEventListener('click', handleUserInteraction);

    return () => {
      fixedBar.removeEventListener('scroll', handleUserInteraction);
      fixedBar.removeEventListener('click', handleUserInteraction);
    };
  }, [curationsData.length]);

  // scroll down check for when to show fixed slug bar
  useEffect(() => {
    if (!containerRef.current) return;

    const currentContainerRef = containerRef.current;

    const handleScrollDirection = (): void => {
      const { scrollTop } = currentContainerRef;
      if (
        Math.abs(scrollTop - lastScrollTop.current) < SCROLL_DIRECTION_THRESHOLD
      ) {
        return;
      }

      const scrollingDown = scrollTop > lastScrollTop.current;
      setIsScrollingUp(scrollTop < lastScrollTop.current);

      // Show install prompt on first meaningful scroll down
      if (scrollingDown && scrollTop > 50) {
        showInstallPrompt();
      }

      lastScrollTop.current = scrollTop;
    };

    currentContainerRef.addEventListener('scroll', handleScrollDirection, {
      passive: true,
    });
    return () =>
      currentContainerRef.removeEventListener('scroll', handleScrollDirection);
  }, [curationsData.length, showInstallPrompt]);

  // intersection check with static slug bar for when to not show slug bar
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => setShowFixedSlugBar(!entry.isIntersecting),
      { rootMargin: `-${HEADER_HEIGHT}px 0px 0px 0px`, threshold: 0 }
    );
    if (slugBarRef.current) {
      observer.observe(slugBarRef.current);
    }
    return () => observer.disconnect();
  }, [curationsData.length]);

  // Scroll to section
  const handleSlugClick = (id: number): void => {
    const el = sectionRefs.current[id];
    if (el && containerRef.current) {
      setLockFixedSlugBar(true);
      const { scrollTop } = containerRef.current;
      const offset =
        HEADER_HEIGHT + (showFixedSlugBar ? SLUG_BAR_HEIGHT : 0) + 8;
      const top = el.getBoundingClientRect().top + scrollTop - offset;
      containerRef.current.scrollTo({ top, behavior: 'smooth' });

      // setTimeout(() => setLockFixedSlugBar(false), SLUG_BAR_LOCK_PERIOD);
    }
  };

  // aligning slug bar scroll with a single source of truth
  useEffect(() => {
    const staticBar = slugBarRef.current;
    const fixedBar = fixedSlugBarRef.current;

    if (!staticBar || !fixedBar) return;

    const handleScrollPosition = (e: Event): void => {
      const target = e.target as HTMLDivElement;
      scrollLeftRef.current = target.scrollLeft;
    };

    // Only listen to the visible bar
    if (showFixedSlugBar) {
      fixedBar.addEventListener('scroll', handleScrollPosition);
      return () => fixedBar.removeEventListener('scroll', handleScrollPosition);
    } else {
      staticBar.addEventListener('scroll', handleScrollPosition);
      return () =>
        staticBar.removeEventListener('scroll', handleScrollPosition);
    }
  }, [showFixedSlugBar, curationsData.length]);

  //updating slug bar scrolls
  useLayoutEffect(() => {
    const staticBar = slugBarRef.current;
    const fixedBar = fixedSlugBarRef.current;

    if (!staticBar || !fixedBar) return;

    if (showFixedSlugBar) {
      fixedBar.scrollLeft = scrollLeftRef.current;
    } else {
      staticBar.scrollLeft = scrollLeftRef.current;
    }
  }, [showFixedSlugBar, curationsData.length]);

  const renderBoldedText = (input: string): JSX.Element => {
    const parts = input.split(/\*\*/);

    return (
      <span>
        {parts.map((part, idx) =>
          idx % 2 === 1 ? (
            <span key={idx} className={styles.highlightedText}>
              {part}
            </span>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  const renderSlugBar = (
    className: string,
    ref: React.RefObject<HTMLDivElement | null>
  ): JSX.Element => {
    return (
      <div className={className} ref={ref}>
        {curationsData.map((curationData) => (
          <div
            key={curationData.id}
            className={styles.slug}
            onClick={() => handleSlugClick(curationData.id)}
          >
            {curationData.title}
          </div>
        ))}
      </div>
    );
  };

  // Intercept product card clicks (but not back button)
  const handleContainerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      const target = e.target as HTMLElement;

      // Check if click is on a product card (Link to /product/)
      const productLink = target.closest('a[href^="/product/"]');
      if (productLink) {
        e.preventDefault();
        e.stopPropagation();
        showInstallPrompt();
      }
    },
    [showInstallPrompt]
  );

  const activeCuration =
    curationsData.find((c) => String(c.id) === id) || curationsData[0];

  const handleOpenApp = useCallback((): void => {
    if (overrideDeepLink && curatorLinkEventPayload) {
      trackEvent(
        EVENTS.CURATOR_LINK_OPEN_IN_APP_CLICKED,
        curatorLinkEventPayload
      );
    }

    openCurationInApp({
      curationId: id ?? '',
      overrideDeepLink: overrideDeepLink ?? activeCuration?.deepLink,
    });
  }, [activeCuration?.deepLink, curatorLinkEventPayload, id, overrideDeepLink]);

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.header}>
        <img
          src={backButtonDark}
          alt="Back"
          className={styles.backIcon}
          onClick={() => navigate(-1)}
        />
      </div>
      {isLoading ? (
        <div>
          {SkeletonLoader.search()}
          {SkeletonLoader.doubleColumnList(8, {
            className: styles.skeletonContent,
          })}
        </div>
      ) : (
        <div onClickCapture={handleContainerClick}>
          <div className={styles.title}>{renderBoldedText(title.current)}</div>
          {curationsData.length > 1 &&
            renderSlugBar(styles.slugContainer, slugBarRef)}
          {curationsData.length > 1 &&
            renderSlugBar(
              `${styles.fixedSlugBar} ${showFixedSlugBar && (isScrollingUp || lockFixedSlugBar) && curationsData.length > 1 ? styles.visible : ''}`,
              fixedSlugBarRef
            )}
          {curationsData.length > 0 && (
            <div className={styles.curationDataContainer}>
              {curationsData.map((curationData) => (
                <div
                  key={curationData.id}
                  ref={(el) => {
                    sectionRefs.current[curationData.id] = el;
                  }}
                >
                  {curationData.curationImage && (
                    <div
                      className={`${styles.imageContainer} ${curationsData.length > 1 ? styles.multipleCurationData : styles.singleCurationData}`}
                    >
                      <img
                        className={styles.curationImage}
                        src={updatedImageUrl(curationData.curationImage)}
                        alt={curationData.title}
                      />
                      {curationData.imageTitle && (
                        <div className={styles.imageTitle}>
                          {curationData.imageTitle}
                        </div>
                      )}
                    </div>
                  )}
                  {curationsData.length > 1 && !curationData.curationImage && (
                    <div className={styles.titleText}>{curationData.title}</div>
                  )}
                  <div
                    className={`${styles.description} ${curationsData.length === 1 ? styles.singleCurationDescription : ''}`}
                  >
                    {curationData.description}
                  </div>
                  <StaticLoadDoubleColumnProductList
                    products={curationData.products}
                    showAll={curationsData.length === 1}
                    displayInitially={PRODUCTS_ON_INITIAL_LOAD}
                    displayOnLoadMore={PRODUCTS_ON_LOAD_MORE}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {showInstallSheet && <CurationInstallSheet onOpenApp={handleOpenApp} />}
    </div>
  );
};

export default CurationPageV2;
